import * as DialogPrimitive from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { forwardRef, isValidElement, useEffect, useState } from 'react';
import Button from '../Button';
import Icon from '../Icon';
import TextField from '../TextField';
import type { ModalProps } from './Modal.types';

// oxlint-disable-next-line no-undef
const CSS_TRANSITION_DURATION = process.env.NODE_ENV === 'test' ? 0 : 300; // Disable animations during testing

const Dialog = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogContent = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className = '', 'aria-describedby': ariaDescribedBy, children, ...props }, ref) => (
  <DialogPortal>
    {/* We will not use DialogPrimitive.Overlay because it is not triggered if we have modal prop set to false, and
     we need to have modal prop set to false, because if not, then if we will have a popover with inputs then the
     focus will be automatically trapped */}
    <div className="tw:fixed tw:inset-0 tw:z-1010 tw:overflow-y-auto">
      <div
        className="
          tw:absolute tw:inset-0 tw:h-full tw:w-full tw:bg-sq-dark-background
          tw:opacity-50 tw:select-none
        "
      />
      <DialogPrimitive.Content
        ref={ref}
        aria-describedby={ariaDescribedBy}
        className={clsx(
          // oxlint-disable-next-line better-tailwindcss/no-unknown-classes
          `tw:duration-${CSS_TRANSITION_DURATION}`,
          `tw:fixed tw:top-0 tw:left-[50%] tw:z-1
           tw:my-6
           tw:grid
           tw:translate-x-[-50%] tw:gap-4 tw:rounded-lg tw:border tw:bg-sq-white tw:shadow-lg
         tw:dark:bg-sq-dark-background`,
          className,
        )}
        {...props}>
        {/*
         Due to some slight changes in ref handling in react 19, `DialogContent` does always contain a `DialogTitle`,
         but some forward ref logic we use here seems to cause the title to not get detected correctly. This doesn't
         impact functionality, but it does spout errors in any unit tests that use the modal, which cause failures.
         This line below is a no-impact way to skip out on the errors.
         */}
        <DialogPrimitive.DialogTitle className="tw:hidden"></DialogPrimitive.DialogTitle>
        {children}
      </DialogPrimitive.Content>
    </div>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div className="tw:w-full tw:justify-between">
    <div {...props} />
  </div>
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>((props, ref) => <DialogPrimitive.Title ref={ref} {...props} />);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>((props, ref) => (
  <DialogPrimitive.Description asChild>
    <div ref={ref} {...props} />
  </DialogPrimitive.Description>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const Modal = ({
  titleIcon,
  title,
  titleSuffixLabel,
  subtitle,
  children,
  open = false,
  onClose,
  customButton = false,
  customButtonLabel = 'Back',
  onClickCustomButton,
  submitButtonLabel = 'Submit',
  cancelButtonLabel = 'Cancel',
  disableSubmitButton = false,
  stopPropagationSubmitButton = false,
  onSubmit,
  isTitleEditable = false,
  onTitleChanged,
  inputExtraClassNames,
  hideCloseIcon = false,
  size = 'xl',
  titleIconPosition = 'left',
  hideFooterButtons = false,
  hideSubmitButton = false,
  hideCancelButton = false,
  testId = 'modal',
  modalFooter,
  dialogClassName,
  titlePlaceholder,
  titleRequired,
  titleError,
  submitButtonTooltip,
  cancelButtonTooltip,
  disableCustomButton,
  customHeader,
  middleFooterSection = <></>,
  customButtonVariant = 'outline',
  submitButtonVariant = 'theme',
  keepFocusInsideModal = true,
  onPointerDownOutside,
  onInteractOutside,
  ariaDescribedBy,
}: ModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  // the Dialog is adding pointerEvents: none to body and the dropdowns from the modal does not work anymore
  useEffect(() => {
    if (open) {
      // Pushing the change to the end of the call stack
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 0);

      return () => clearTimeout(timer);
    } else {
      document.body.style.pointerEvents = 'auto';
    }
  }, [open]);

  const handleSubmit = async (e: React.MouseEvent) => {
    if (!onSubmit) return;
    try {
      setIsLoading(true);
      await onSubmit(e);
    } finally {
      setIsLoading(false);
    }
  };

  const renderTitle = () => {
    let titleIconElement = <></>;

    if (titleIcon) {
      if (isValidElement(titleIcon)) {
        titleIconElement = <div className="tw:mt-2">{titleIcon}</div>;
      } else {
        titleIconElement = (
          <Icon
            icon={titleIcon}
            testId="modalTitleIcon"
            extraClassNames="tw:flex tw:text-xl tw:mt-1 tw:!leading-normal"
          />
        );
      }
    }

    return (
      <>
        {titleIcon && titleIconPosition === 'left' ? <div className="tw:mr-2 tw:flex">{titleIconElement}</div> : <></>}
        <DialogTitle asChild>
          {isTitleEditable ? (
            <div className="tw:flex tw:w-full tw:items-center">
              <TextField
                extraClassNames={inputExtraClassNames}
                value={title}
                type="text"
                testId="modalTitle"
                onChange={onTitleChanged}
                placeholder={titlePlaceholder}
                required={titleRequired}
                showError={!!titleError}
              />
              {titleError && <p className="tw:mb-0 tw:min-w-fit tw:pl-2 tw:text-sq-danger">{titleError}</p>}
            </div>
          ) : (
            (customHeader ?? (
              // oxlint-disable-next-line better-tailwindcss/no-unknown-classes
              <div data-testid="modalTitle" className="modal-title">
                <div className="tw:flex tw:items-center">
                  <h3>{title}</h3>
                  {titleSuffixLabel && <span className="tw:pl-0.5 tw:text-xl">{titleSuffixLabel}</span>}
                </div>
                {subtitle && (
                  <div className="tw:mt-1 tw:text-left tw:text-sm tw:italic" data-testid="modal-subtitle">
                    {subtitle}
                  </div>
                )}
              </div>
            ))
          )}
        </DialogTitle>
        {titleIcon && titleIconPosition === 'right' ? <div className="tw:ml-4 tw:flex">{titleIconElement}</div> : <></>}
      </>
    );
  };

  return open ? (
    <Dialog open={true} onOpenChange={onClose} modal={keepFocusInsideModal}>
      <DialogContent
        onPointerDownOutside={(e) => (onPointerDownOutside ? onPointerDownOutside(e) : e.preventDefault())}
        onInteractOutside={(e) => (onInteractOutside ? onInteractOutside(e) : e.preventDefault())}
        data-testid={testId}
        aria-describedby={ariaDescribedBy}
        className={clsx(
          // oxlint-disable-next-line better-tailwindcss/no-unknown-classes
          'modalContent',
          'tw:w-full tw:gap-0! tw:border-0 tw:shadow-none tw:dark:border tw:dark:border-gray-500 tw:dark:text-sq-dark-text',
          {
            'tw:max-w-xs': size === 'xs',
            'tw:max-w-sm': size === 'sm',
            'tw:max-w-md': size === 'md',
            'tw:max-w-lg': size === 'lg',
            'tw:max-w-xl': size === 'xl',
            'tw:max-w-2xl': size === '2xl',
            'tw:max-w-3xl': size === '3xl',
            'tw:max-w-4xl': size === '4xl',
            'tw:max-w-5xl': size === '5xl',
            'tw:max-w-6xl': size === '6xl',
          },
          dialogClassName,
        )}>
        <DialogHeader
          className={clsx(
            // oxlint-disable-next-line better-tailwindcss/no-unknown-classes
            'modal-header',
            'tw:flex tw:w-full tw:justify-between tw:border-0 tw:border-b tw:border-sq-disabled-gray tw:px-6 tw:py-4 tw:dark:border-sq-dark-disabled-gray',
          )}>
          <div className="tw:flex tw:w-full">{renderTitle()}</div>
          {!hideCloseIcon && (
            <DialogClose
              autoFocus={false}
              className={clsx(
                // oxlint-disable-next-line better-tailwindcss/no-unknown-classes
                'close',
                'tw:ml-4 tw:cursor-pointer tw:bg-transparent tw:text-base tw:opacity-70 tw:hover:opacity-100',
              )}
              data-testid="closeButton">
              <span className="tw:cursor-pointer">&times;</span>
            </DialogClose>
          )}
        </DialogHeader>
        <DialogDescription
          className={clsx(
            // oxlint-disable-next-line better-tailwindcss/no-unknown-classes
            'modal-body',
            'tw:flex-1 tw:overflow-y-auto tw:px-6 tw:py-4',
          )}>
          {children}
        </DialogDescription>
        {!hideFooterButtons && (
          <DialogFooter
            className={clsx(
              // oxlint-disable-next-line better-tailwindcss/no-unknown-classes
              'modal-footer',
              'tw:px-6 tw:py-4',
            )}>
            {modalFooter ?? (
              <div className="tw:flex tw:w-full tw:justify-between" data-testid="modalFooter">
                <div className="tw:flex tw:justify-start">
                  {customButton && (
                    <Button
                      label={customButtonLabel}
                      onClick={onClickCustomButton}
                      disabled={disableCustomButton}
                      extraClassNames="tw:justify-start tw:min-w-25"
                      testId="customButton"
                      variant={customButtonVariant}
                    />
                  )}
                </div>
                <div className="tw:flex tw:justify-end">
                  <div className="tw:flex tw:items-center">{middleFooterSection}</div>
                  {!hideCancelButton && (
                    <DialogClose asChild>
                      <Button
                        label={cancelButtonLabel}
                        extraClassNames="tw:mr-5 tw:min-w-25"
                        tooltip={cancelButtonTooltip}
                        variant="outline"
                        stopPropagation={false}
                        testId="cancelButton"
                      />
                    </DialogClose>
                  )}
                  {!hideSubmitButton && (
                    <Button
                      label={submitButtonLabel}
                      onClick={handleSubmit}
                      disabled={disableSubmitButton || isLoading}
                      variant={submitButtonVariant}
                      stopPropagation={stopPropagationSubmitButton}
                      tooltip={submitButtonTooltip}
                      inProgress={isLoading}
                      iconPosition="left"
                      testId="submitButton"
                      extraClassNames="tw:min-w-25"
                    />
                  )}
                </div>
              </div>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  ) : (
    <></>
  );
};

export default Modal;
